import { createBrowserRouter, Navigate } from 'react-router-dom'
import { AppShell } from './components/AppShell'
import { AuthLayout } from './components/AuthLayout'
import { RequireAuth } from './auth/RequireAuth'
import { RequireClub } from './auth/RequireClub'
import { RequireConsent } from './auth/RequireConsent'
import { RequireStaffTerms } from './auth/RequireStaffTerms'
import { RequireAdminShell, RequireDeviceAdmin, RequireSuperadmin } from './auth/RequireSuperadmin'
import { PlayBridge } from './pages/PlayBridge'
import { ConsentGate } from './pages/ConsentGate'
import { NoClubPage } from './pages/NoClubPage'
import { InvitePage } from './pages/InvitePage'
import { StaffInvitePage } from './pages/StaffInvitePage'
import { LoginPage } from './pages/LoginPage'
import { NotFoundPage } from './pages/NotFoundPage'
import { TermsPage, DataPolicyPage } from './pages/legal/LegalPage'
import { KnowLayout } from './pages/data/KnowLayout'
import { PlayerReportPage } from './pages/data/reports/PlayerReportPage'
import { GoldReportPage } from './pages/data/reports/GoldReportPage'
import { KnowHome } from './pages/data/KnowHome'
import { KnowProfile } from './pages/data/KnowProfile'
import { KnowQuestionnaire } from './pages/data/KnowQuestionnaire'
import { RedirectKnowToData } from './pages/RedirectKnowToData'
import { AdminLayout } from './pages/admin/AdminLayout'
import { AdminIndex } from './pages/admin/AdminIndex'
import { AdminUsers } from './pages/admin/AdminUsers'
import { AdminUserDetailPage } from './pages/admin/AdminUserDetail'
import { AdminAthleteTimeline } from './pages/admin/AdminAthleteTimeline'
import { AdminCohort } from './pages/admin/AdminCohort'
import { StaffReportPage } from './pages/data/reports/StaffReportPage'
import { AdminSessions } from './pages/admin/AdminSessions'
import { AdminSessionDetailPage } from './pages/admin/AdminSessionDetail'
import { AdminQuestionnaires } from './pages/admin/AdminQuestionnaires'
import { AdminQuestionnaireDetailPage } from './pages/admin/AdminQuestionnaireDetail'
import { AdminQuestionnaireViewer } from './pages/admin/AdminQuestionnaireViewer'
import { AdminInvitations } from './pages/admin/AdminInvitations'
import { AdminEvents } from './pages/admin/AdminEvents'
import { AdminEmails } from './pages/admin/AdminEmails'
import { AdminClubs } from './pages/admin/AdminClubs'
import { AdminClubDetail } from './pages/admin/AdminClubDetail'
import { AdminSetupGuide } from './pages/admin/AdminSetupGuide'
import { AdminApps } from './pages/admin/AdminApps'

import { wikiRoutes } from './wikiRoutes'
import { ClubPage } from './pages/club/ClubPage'
import { ClubPlayerResults } from './pages/club/ClubPlayerResults'
import { PolarCheckPanel } from './pages/device-panel/PolarCheckPanel'
import { SetupGuide } from './pages/SetupGuide'
import { SetPin } from './pages/SetPin'
import { TabletLayout } from './tablet/TabletLayout'
import { TabletEntry } from './tablet/TabletEntry'
import { TabletQuestionnaire } from './tablet/TabletQuestionnaire'
import { TabletProfile } from './tablet/TabletProfile'
import { TabletCheckIn } from './tablet/TabletCheckIn'

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <AuthLayout><LoginPage /></AuthLayout>,
  },
  {
    // Aterrizaje de links de invitación admin. El componente resuelve el
    // token y bouncea a /login (si no hay session) o a /data/{code} (si
    // hay session con el email correcto). Pública — el token es la
    // credencial.
    path: '/invite/:token',
    element: <InvitePage />,
  },
  {
    // Self-signup del primer manager/técnico de un club — el token ES la
    // credencial de alta (POST /staff-invites/{token}/register). Pública.
    path: '/staff-invite/:token',
    element: <StaffInvitePage />,
  },
  {
    // `/know` se renombró a `/data` (mg, 2026-09-09: "Know" pasa a llamarse
    // "Data" en toda la barra y en la URL). Redirect indefinido, no un shim
    // temporal — ver `pages/RedirectKnowToData.tsx`: hay mails de invitación
    // ya enviados y bookmarks de usuarios con `/know/...`.
    path: '/know',
    element: <RedirectKnowToData />,
  },
  {
    path: '/know/*',
    element: <RedirectKnowToData />,
  },
  {
    // `RequireClub` por dentro de `RequireAuth`: una cuenta sin club no ve el
    // área personal, ve `/sin-club` (mg, 2026-09-09).
    path: '/data',
    element: (
      <RequireAuth>
        <RequireClub>
          <KnowLayout />
        </RequireClub>
      </RequireAuth>
    ),
    children: [
      // /data ya no es el catálogo de auto-servicio: es el área personal del
      // jugador, UNA sola página jerarquizada (cabecera → ranking →
      // performance → sesiones → cuestionarios/perfil). /data/dashboard,
      // /data/performance y /data/physiology quedaron absorbidas por ella —
      // redirects para links viejos, con ancla a la sección equivalente. Las
      // señales ya no son una sección propia: en vivo es una card de arriba,
      // el histórico está adentro de cada sesión (#sesiones).
      { index: true, element: <KnowHome /> },
      { path: 'reports', element: <GoldReportPage /> },
      { path: 'reports/detail', element: <PlayerReportPage /> },
      { path: 'reports/sessions/:sessionId', element: <GoldReportPage /> },
      { path: 'reports/sessions/:sessionId/trials/:trialId', element: <GoldReportPage /> },
      { path: 'dashboard', element: <Navigate to="/data" replace /> },
      { path: 'performance', element: <Navigate to="/data#performance" replace /> },
      { path: 'physiology', element: <Navigate to="/data#sesiones" replace /> },
      { path: 'profile', element: <KnowProfile /> },
      // `/data/pre-session` se borró (mg, 2026-09-07): el chequeo previo es
      // obligatorio por sesión y se responde DENTRO del vr-app, en el gate
      // "Chequeo previo". Acá sólo se listan las respuestas, en la sección
      // Cuestionarios de `/data`.
      { path: 'pre-session', element: <Navigate to="/data#cuestionarios" replace /> },
      // Consentimiento ANTES del cuestionario (doc 32 §4): el guard rebota
      // al gate de `/` con `?next=` si todavía no está firmado, incluso
      // cuando se entra por link directo o por el redirect del OAuth. El
      // mismo componente en `/tablet` NO lleva este guard — la tablet tiene
      // su propio paso de consentimiento en `TabletLayout`.
      {
        path: ':code',
        element: (
          <RequireConsent>
            <KnowQuestionnaire />
          </RequireConsent>
        ),
      },
    ],
  },
  {
    // MODO TABLET — la tablet compartida que viene en el kit del club.
    // Pública como `/login`: la credencial es el código del kit (que
    // autentica al DISPOSITIVO con el club y queda persistido) + el PIN de 6
    // dígitos del jugador, así que no puede ir detrás de `RequireAuth`.
    // Aditiva: el flujo personal (`/login`, `/invite`, `/data` desde el
    // celular) no cambia.
    path: '/tablet',
    element: <TabletLayout />,
    children: [
      { index: true, element: <TabletEntry /> },
      { path: 'profile', element: <TabletProfile /> },
      { path: 'checkin', element: <TabletCheckIn /> },
      // El cuestionario del jugador corre acá adentro (mismo componente que
      // `/data/:code`) para que el auto-logout entre jugadores y el watchdog
      // de inactividad del layout sigan vivos mientras responde.
      { path: 'data/:code', element: <TabletQuestionnaire /> },
    ],
  },
  ...wikiRoutes,
  {
    path: '/admin',
    element: (
      <RequireAdminShell>
        <AdminLayout />
      </RequireAdminShell>
    ),
    children: [
      { index: true, element: <AdminIndex /> },
      { path: 'users', element: <AdminUsers /> },
      { path: 'users/:userId', element: <AdminUserDetailPage /> },
      { path: 'athletes/:userId', element: <AdminAthleteTimeline /> },
      { path: 'athletes/:userId/report', element: <StaffReportPage /> },
      { path: 'cohorte', element: <RequireSuperadmin><AdminCohort /></RequireSuperadmin> },
      { path: 'sessions', element: <AdminSessions /> },
      { path: 'sessions/:sessionId', element: <AdminSessionDetailPage /> },
      { path: 'questionnaires', element: <AdminQuestionnaires /> },
      { path: 'questionnaires/:responseId', element: <AdminQuestionnaireDetailPage /> },
      // `questionnaire-editor` dejó de ser editor (mg, 2026-09-08): es el
      // visor inmutable de TODOS los cuestionarios. La ruta se conserva
      // porque es la que está linkeada y bookmarkeada; la del editor de
      // versiones rebota al visor.
      { path: 'questionnaire-editor', element: <AdminQuestionnaireViewer /> },
      {
        path: 'questionnaire-editor/*',
        element: <Navigate to="/admin/questionnaire-editor" replace />,
      },
      { path: 'invitations', element: <AdminInvitations /> },
      { path: 'events', element: <AdminEvents /> },
      { path: 'emails', element: <AdminEmails /> },
      { path: 'clubs', element: <AdminClubs /> },
      { path: 'clubs/:clubId', element: <AdminClubDetail /> },
      // Guía de setup del equipo: la ve todo el que entra a /admin (negocio o sistemas).
      { path: 'setup', element: <AdminSetupGuide /> },
      // Apps nativas en la Meta Horizon Store: sólo administradores de negocio.
      { path: 'apps', element: <RequireSuperadmin><AdminApps /></RequireSuperadmin> },
      {
        // Gated by the systems-admin role (device_admin) or a full superadmin.
        // Since 2026-09-28 (no base in the club setup) it is only the Polar
        // bench check; the RPi/kit panel (`DevicePanelContainer`) stays in the
        // code as the edge opt-in path (ADR 0002), unrouted.
        path: 'devices',
        element: (
          <RequireDeviceAdmin>
            <PolarCheckPanel />
          </RequireDeviceAdmin>
        ),
      },
    ],
  },
  {
    path: '/club',
    element: (
      <RequireAuth>
        <RequireStaffTerms>
          <ClubPage />
        </RequireStaffTerms>
      </RequireAuth>
    ),
  },
  {
    // Gateado con el mismo `RequireStaffTerms` que /club — sin esto, un
    // manager que tiene el link/bookmark de un jugador puntual podría
    // esquivar el gate de T&C entrando directo acá (pedido de mg 2026-07-09,
    // "bloqueante de verdad": confirmar que no hay forma de esquivarlo
    // navegando a otra URL del panel).
    path: '/club/players/:userId',
    element: (
      <RequireAuth>
        <RequireStaffTerms>
          <ClubPlayerResults />
        </RequireStaffTerms>
      </RequireAuth>
    ),
  },
  {
    path: '/club/:clubId/players/:userId/report',
    element: <RequireAuth><RequireStaffTerms><AppShell /></RequireStaffTerms></RequireAuth>,
    children: [{ index: true, element: <StaffReportPage /> }],
  },
  {
    // Legacy top-level device panel → now lives under /admin/devices.
    path: '/device-panel',
    element: <Navigate to="/admin/devices" replace />,
  },
  {
    path: '/',
    element: <AppShell />,
    children: [
      {
        // `/` para un jugador logueado NO es una home: es el bridge a la
        // superficie de juego (lobby del vr-app, con Comenzar + ranking y
        // los gates adentro). Ver `pages/PlayBridge.tsx`.
        index: true,
        element: (
          <RequireAuth>
            <RequireClub>
              <PlayBridge />
            </RequireClub>
          </RequireAuth>
        ),
      },
      {
        // Única superficie de una cuenta sin club: la pantalla que le explica
        // que tiene que pedir el alta a su club, más el ranking. Acá rebota
        // `RequireClub` y acá aterriza `postLoginRoute` (mg, 2026-09-09).
        path: 'sin-club',
        element: (
          <RequireAuth>
            <NoClubPage />
          </RequireAuth>
        ),
      },
      {
        // Gate de consentimiento del camino de CUESTIONARIOS en la web (acá
        // rebota `RequireConsent`), con el destino colgado en `?next=`. El
        // camino de JUEGO no pasa por acá: el vr-app tiene su propio gate.
        path: 'consent',
        element: (
          <RequireAuth>
            <ConsentGate />
          </RequireAuth>
        ),
      },
      // Aterrizaje del link de setup/reset de PIN (mail). Pública — el
      // token ES la credencial. Montada en AppShell (footer + topbar
      // globales, ver CLAUDE.md) en vez del AuthLayout pre-login: Topbar y
      // AppNavBar ya toleran `status === 'anonymous'` sin romper.
      { path: 'guia', element: <SetupGuide /> },
      { path: 'set-pin', element: <SetPin /> },
      // Documentos legales, públicos: los linkea el modal "Acerca de"
      // (`AboutButton`, rutas `ABOUT.termsPath` / `ABOUT.dataPolicyPath`).
      { path: 'legal/terminos', element: <TermsPage /> },
      { path: 'legal/datos', element: <DataPolicyPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
])
